package com.iregained.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.iregained.site.components.sections.footer.MainFooter
import com.iregained.site.components.sections.header.NavHeader
import com.iregained.site.components.widgets.RegionSwitcherBanner
import com.iregained.site.region.Region
import com.iregained.site.region.RegionConfig
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.events.EventListener
import kotlin.time.Duration.Companion.milliseconds

// GLOBAL FLAG: Tracks if the current route change was caused by the browser's Back/Forward buttons
private var isNavigatingViaBrowserHistory = false

@Composable
fun PageLayout(content: @Composable () -> Unit) {

    val ctx = rememberPageContext()

    // POPSTATE LISTENER: Detects Back/Forward button usage
    DisposableEffect(Unit) {
        val handlePopState = EventListener {
            isNavigatingViaBrowserHistory = true
        }
        window.addEventListener("popstate", handlePopState)

        onDispose {
            window.removeEventListener("popstate", handlePopState)
        }
    }

    // SCROLL TRACKER: Continuously save the exact Y-axis pixel position in the browser's Session Storage
    DisposableEffect(ctx.route.path) {
        onDispose {
            // This fires exactly once when the route changes, entirely eliminating scroll lag!
            window.sessionStorage.setItem(ctx.route.path, window.scrollY.toString())
        }
    }

    // SCROLL RESTORATION: Combats Layout Shift from loading images across all pages
    LaunchedEffect(ctx.route.path, ctx.route.fragment) {
        val fragment = ctx.route.fragment

        if (!fragment.isNullOrBlank()) {
            // SCENARIO A: URL has a #fragment (e.g., clicking a specific anchor link)
            delay(100.milliseconds)
            document.getElementById(fragment)?.asDynamic().scrollIntoView(
                js("{ behavior: 'smooth', block: 'start' }")
            )
        } else {
            // SCENARIO B: Standard page navigation
            if (isNavigatingViaBrowserHistory) {
                // Instantly restore scroll without looping and fighting the user
                val savedScrollY = window.sessionStorage.getItem(ctx.route.path)?.toDoubleOrNull() ?: 0.0
                window.scrollTo(js("{ top: savedScrollY, behavior: 'instant' }"))
                isNavigatingViaBrowserHistory = false
            } else {
                // Instantly teleport to top on fresh load
                window.scrollTo(js("{ top: 0, behavior: 'instant' }"))
            }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("min-height", "100dvh") },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // NEW: Place the dynamic Region Switcher Banner at the very top of every page layout
        // This is where you would call the widget you just made.
        if (RegionConfig.currentRegion != Region.CANADA) {
            RegionSwitcherBanner()
        }

        NavHeader() // This puts your menu at the top of every page

        Box(modifier = Modifier.fillMaxWidth().flexGrow(1)) {
            content() // This is where your page-specific code goes
        }

        MainFooter()
    }
}